import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isHumanAgentInboxLive, patchSessionTeamInbox } from "@/api/inboxAgentApi";
import { useSlaSettings } from "@/hooks/useSlaSettings";
import { useWorkspaceRoles } from "@/hooks/useWorkspaceRoles";
import { evaluateChatSlaBreach, evaluateTicketSlaBreach } from "@/lib/slaBreach";
import { sortChatsForInbox, sortTicketsForInbox, getChatLastActivityMs } from "@/lib/inboxSort";
import TeamInboxFolderModal from "./TeamInboxFolderModal";

function openedByLabel(code) {
  if (code === "bot") return "Bot";
  if (code === "user") return "Customer";
  return "Agent";
}

function ticketStatusBadgeClass(status) {
  if (status === "pending") return "bg-amber-100 text-amber-900 ring-amber-200/80";
  if (status === "on_hold") return "bg-sky-100 text-sky-900 ring-sky-200/80";
  if (status === "resolved") return "bg-slate-200/90 text-slate-700 ring-slate-300/80";
  return "bg-emerald-50 text-emerald-900 ring-emerald-200/70";
}

/** @param {string | null | undefined} status */
function isClosedChatStatus(status) {
  const s = String(status ?? "").trim().toLowerCase();
  return s === "resolved" || s === "closed" || s === "completed";
}

const CHAT_STATUS_FILTERS = [
  { id: "open", label: "Open" },
  { id: "closed", label: "Closed" },
  { id: "all", label: "All" },
];

export default function ConversationList({
  selectedId,
  selectedTicketId,
  onSelectConversation,
  onSelectTicket,
  activeCategory,
  onCategoryChange,
  mode = "chats",
  onModeChange,
  showModeToggle = true,
  panelTitle = "Inbox",
  panelSubtitle = "Prioritized queue",
  tickets = [],
  conversations,
  inboxCategories,
  teamInboxes,
  listLoading,
  listError,
  onRetryList,
  hasMoreConversations = false,
  loadingMoreConversations = false,
  onLoadMoreConversations,
  ticketQueueSections,
  ticketsLoading,
  activeWorkspaceId = null,
  workspaces = [],
  onFoldersChanged,
}) {
  const { settings, now } = useSlaSettings();
  const { isAdmin, loading: rolesLoading } = useWorkspaceRoles();
  const [slaBreachOnly, setSlaBreachOnly] = useState(false);
  const [chatStatusFilter, setChatStatusFilter] = useState(/** @type {"open" | "closed" | "all"} */ ("open"));
  const [chatStatusMenuOpen, setChatStatusMenuOpen] = useState(false);
  const chatStatusMenuRef = useRef(null);
  const [teamInboxesExpanded, setTeamInboxesExpanded] = useState(true);
  /** Collapsible block: Chats/Tickets, Views, team folders (below the chat list). Opt-in per session. */
  const [inboxChromeExpanded, setInboxChromeExpanded] = useState(false);
  const [draggingConversationId, setDraggingConversationId] = useState(null);
  const [dropTargetTeamId, setDropTargetTeamId] = useState(null);
  const [teamMoveBusy, setTeamMoveBusy] = useState(false);
  const [teamMoveError, setTeamMoveError] = useState(null);
  const [collapsedTicketSections, setCollapsedTicketSections] = useState({});
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState(/** @type {"create" | "edit"} */ ("create"));
  const [folderModalTarget, setFolderModalTarget] = useState(null);
  const teamMoveErrorTimerRef = useRef(null);

  const canManageTeamFolders = isHumanAgentInboxLive() && !rolesLoading && isAdmin;
  const sections = useMemo(() => ticketQueueSections ?? [], [ticketQueueSections]);
  const isTicketsMode = mode === "tickets";

  const canPersistTeamMove =
    isHumanAgentInboxLive() && !!(activeWorkspaceId && String(activeWorkspaceId).trim());

  const clearTeamMoveErrorLater = useCallback(() => {
    if (teamMoveErrorTimerRef.current) window.clearTimeout(teamMoveErrorTimerRef.current);
    teamMoveErrorTimerRef.current = window.setTimeout(() => {
      setTeamMoveError(null);
      teamMoveErrorTimerRef.current = null;
    }, 5000);
  }, []);

  useEffect(
    () => () => {
      if (teamMoveErrorTimerRef.current) window.clearTimeout(teamMoveErrorTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (!chatStatusMenuOpen) return undefined;
    const onPointerDown = (e) => {
      if (chatStatusMenuRef.current && !chatStatusMenuRef.current.contains(e.target)) {
        setChatStatusMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setChatStatusMenuOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [chatStatusMenuOpen]);

  useEffect(() => {
    if (!isTicketsMode || sections.length === 0) return;
    setCollapsedTicketSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const section of sections) {
        if (!(section.id in next)) {
          next[section.id] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [sections, isTicketsMode]);

  const DROP_NONE = "__none__";

  const openCreateTeamInboxModal = useCallback(() => {
    setFolderModalMode("create");
    setFolderModalTarget(null);
    setFolderModalOpen(true);
    setTeamInboxesExpanded(true);
    setInboxChromeExpanded(true);
  }, []);

  const handleMoveToTeamInbox = useCallback(
    async (sessionId, teamInboxId) => {
      const sid = (sessionId || "").trim();
      if (!sid) {
        setDraggingConversationId(null);
        setDropTargetTeamId(null);
        return;
      }
      if (!isHumanAgentInboxLive() || !activeWorkspaceId || !String(activeWorkspaceId).trim()) {
        setTeamMoveError("Pick a workspace and use the live agent inbox to move chats into team folders.");
        clearTeamMoveErrorLater();
        setDraggingConversationId(null);
        setDropTargetTeamId(null);
        return;
      }
      setTeamMoveBusy(true);
      setTeamMoveError(null);
      try {
        await patchSessionTeamInbox({
          sessionId: sid,
          teamInboxId,
          workspaceId: activeWorkspaceId,
        });
        onRetryList?.();
      } catch (e) {
        setTeamMoveError(e instanceof Error ? e.message : String(e));
        clearTeamMoveErrorLater();
      } finally {
        setTeamMoveBusy(false);
        setDraggingConversationId(null);
        setDropTargetTeamId(null);
      }
    },
    [activeWorkspaceId, onRetryList, clearTeamMoveErrorLater],
  );

  const handleConversationDragEnd = useCallback(() => {
    setDraggingConversationId(null);
    setDropTargetTeamId(null);
  }, []);

  const isTeamInboxUuid = useMemo(
    () => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(activeCategory || "").trim()),
    [activeCategory],
  );

  const filteredChats = useMemo(() => {
    const rows = conversations ?? [];
    if (isHumanAgentInboxLive()) {
      if (activeCategory === "dashboard") return [];
      if (
        activeCategory === "all" ||
        activeCategory === "your-inbox" ||
        activeCategory === "unassigned" ||
        activeCategory === "bot-chats" ||
        isTeamInboxUuid
      ) {
        return rows;
      }
      const teamInbox = (teamInboxes ?? []).find((t) => t.id === activeCategory);
      if (teamInbox) {
        const teamName = teamInbox.name || teamInbox.label;
        return rows.filter(
          (c) => c.team === teamName || c.team === teamInbox.label || c.team.startsWith(String(teamName).replace("...", "")),
        );
      }
      return rows;
    }
    return rows.filter((c) => {
      if (activeCategory === "all") return true;
      if (activeCategory === "your-inbox") return c.assignee === "Neelov";
      if (activeCategory === "unassigned") return c.assignee === "Unassigned";
      if (activeCategory === "bot-chats") return true;
      if (activeCategory === "dashboard") return false;
      const teamInbox = (teamInboxes ?? []).find((t) => t.id === activeCategory);
      if (teamInbox) return c.team === teamInbox.label || c.team.startsWith(teamInbox.label.replace("...", ""));
      return true;
    });
  }, [conversations, activeCategory, teamInboxes, isTeamInboxUuid]);

  const isBotMessagesView = activeCategory === "bot-chats";

  const statusFilteredChats = useMemo(() => {
    if (chatStatusFilter === "all") return filteredChats;
    if (chatStatusFilter === "closed") {
      return filteredChats.filter((c) => isClosedChatStatus(c.status));
    }
    return filteredChats.filter((c) => !isClosedChatStatus(c.status));
  }, [filteredChats, chatStatusFilter]);

  /** Human inbox: SLA-first. Bot Messages: newest activity only (no SLA). */
  const orderedChats = useMemo(() => {
    if (isBotMessagesView) {
      return [...statusFilteredChats].sort(
        (a, b) => getChatLastActivityMs(b) - getChatLastActivityMs(a),
      );
    }
    return sortChatsForInbox(statusFilteredChats, settings, now);
  }, [statusFilteredChats, settings, now, isBotMessagesView]);

  const openChatCount = useMemo(
    () => filteredChats.filter((c) => !isClosedChatStatus(c.status)).length,
    [filteredChats]
  );
  const closedChatCount = useMemo(
    () => filteredChats.filter((c) => isClosedChatStatus(c.status)).length,
    [filteredChats]
  );

  // Open is the agent's live queue and is meant to read as complete, so it never
  // offers paging. Closed and All still page through history.
  const showLoadMoreConversations =
    !isTicketsMode && !listLoading && hasMoreConversations && chatStatusFilter !== "open";

  const chatStatusFilterLabel = useMemo(() => {
    const row = CHAT_STATUS_FILTERS.find((f) => f.id === chatStatusFilter);
    return row?.label ?? "Open";
  }, [chatStatusFilter]);

  const chatStatusFilterCount = useMemo(() => {
    if (chatStatusFilter === "closed") return closedChatCount;
    if (chatStatusFilter === "all") return filteredChats.length;
    return openChatCount;
  }, [chatStatusFilter, openChatCount, closedChatCount, filteredChats.length]);

  const chatSlaById = useMemo(() => {
    const m = new Map();
    if (isBotMessagesView) return m;
    for (const c of orderedChats) {
      m.set(c.id, evaluateChatSlaBreach(c.slaMeta ?? {}, settings, now, c.status));
    }
    return m;
  }, [orderedChats, settings, now, isBotMessagesView]);

  const ticketSlaById = useMemo(() => {
    const m = new Map();
    for (const t of tickets ?? []) {
      m.set(t.id, evaluateTicketSlaBreach(t, settings, now));
    }
    return m;
  }, [tickets, settings, now]);

  const chatBreaches = useMemo(() => orderedChats.filter((c) => chatSlaById.get(c.id)?.breached).length, [orderedChats, chatSlaById]);
  const ticketBreaches = useMemo(
    () => (tickets ?? []).filter((t) => ticketSlaById.get(t.id)?.breached).length,
    [tickets, ticketSlaById]
  );

  const filteredChatsForList =
    slaBreachOnly && !isBotMessagesView
      ? orderedChats.filter((c) => chatSlaById.get(c.id)?.breached)
      : orderedChats;

  const activeNavLabel = useMemo(() => {
    if (activeCategory === "dashboard") return "Dashboard";
    const cat = (inboxCategories ?? []).find((c) => c.id === activeCategory);
    if (cat) return cat.label;
    const team = (teamInboxes ?? []).find((t) => t.id === activeCategory);
    if (team) return team.label;
    return activeCategory;
  }, [activeCategory, inboxCategories, teamInboxes]);

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col bg-white/95 backdrop-blur-md dark:bg-slate-900/95">
      {/* Chats / tickets list first (primary column area) */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-b border-slate-200/60 dark:border-slate-700/60">
        <div className="shrink-0 border-b border-slate-200/80 bg-slate-50/50 dark:border-slate-700/60 dark:bg-slate-900/60">
          <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2 text-xs">
            {isTicketsMode ? (
              <>
                <span className="font-semibold text-slate-800 dark:text-slate-200">{tickets.length} ticket{tickets.length === 1 ? "" : "s"}</span>
                <span className="text-slate-300 dark:text-slate-600">·</span>
                <span className="font-semibold text-rose-700 dark:text-rose-400">{ticketBreaches} SLA</span>
              </>
            ) : (
              <>
                <div className="relative" ref={chatStatusMenuRef}>
                  <button
                    type="button"
                    onClick={() => setChatStatusMenuOpen((v) => !v)}
                    aria-haspopup="listbox"
                    aria-expanded={chatStatusMenuOpen}
                    title="Filter by open or closed chats"
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-white dark:text-slate-200 dark:ring-slate-600 dark:hover:bg-slate-800"
                  >
                    <span>
                      {chatStatusFilterCount} {chatStatusFilterLabel.toLowerCase()}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-sm text-slate-500 transition-transform dark:text-slate-400 ${
                        chatStatusMenuOpen ? "rotate-180" : ""
                      }`}
                      aria-hidden
                    />
                  </button>
                  {chatStatusMenuOpen && (
                    <div
                      role="listbox"
                      aria-label="Chat status filter"
                      className="absolute left-0 z-20 mt-1 min-w-[9.5rem] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-600 dark:bg-slate-800"
                    >
                      {CHAT_STATUS_FILTERS.map((opt) => {
                        const count =
                          opt.id === "open"
                            ? openChatCount
                            : opt.id === "closed"
                              ? closedChatCount
                              : filteredChats.length;
                        const selected = chatStatusFilter === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            onClick={() => {
                              setChatStatusFilter(opt.id);
                              setChatStatusMenuOpen(false);
                            }}
                            className={`flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-xs transition-colors ${
                              selected
                                ? "bg-teal-50 font-semibold text-teal-900 dark:bg-teal-950/50 dark:text-teal-200"
                                : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700/60"
                            }`}
                          >
                            <span>{opt.label}</span>
                            <span className="tabular-nums text-slate-400 dark:text-slate-500">{count}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {!isBotMessagesView && (
                  <>
                    <span className="text-slate-300 dark:text-slate-600">·</span>
                    <span className="font-semibold text-rose-700 dark:text-rose-400">{chatBreaches} SLA</span>
                  </>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {!isBotMessagesView && (
            <button
              type="button"
              onClick={() => setSlaBreachOnly((v) => !v)}
              aria-pressed={slaBreachOnly}
              title="Show only chats or tickets that breach SLA targets"
              className={`rounded-lg px-2 py-1 text-[10px] font-bold uppercase tracking-wide ring-1 transition-colors ${
                slaBreachOnly ? "bg-rose-600 text-white ring-rose-700" : "bg-white text-rose-700 ring-rose-200 hover:bg-rose-50 dark:bg-slate-800 dark:text-rose-400 dark:ring-rose-800/60 dark:hover:bg-rose-950/40"
              }`}
            >
              SLA breach
            </button>
            )}
          </div>
          </div>
        </div>
        <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto pb-2">
          {listError && (
            <div className="mx-3 mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-rose-200 bg-rose-50/90 px-3 py-2 text-xs text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">
              <span>{listError.message}</span>
              {onRetryList && (
                <button type="button" onClick={onRetryList} className="font-semibold underline">
                  Retry
                </button>
              )}
            </div>
          )}
          {!isTicketsMode && listLoading && conversations.length === 0 && (
            <div className="space-y-2 px-4 py-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex animate-pulse gap-3">
                  <div className="h-11 w-11 shrink-0 rounded-full bg-slate-200 dark:bg-slate-700" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="h-3 w-1/2 rounded bg-slate-200 dark:bg-slate-700" />
                    <div className="h-2 w-full rounded bg-slate-100 dark:bg-slate-800" />
                  </div>
                </div>
              ))}
            </div>
          )}
          {!isTicketsMode &&
            !listLoading &&
            filteredChatsForList.length === 0 &&
            slaBreachOnly && (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">No SLA breaches in this view.</p>
            )}
          {!isTicketsMode &&
            !listLoading &&
            filteredChatsForList.length === 0 &&
            !slaBreachOnly && (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                {chatStatusFilter === "closed"
                  ? "No closed chats in this view."
                  : chatStatusFilter === "all"
                    ? "No chats in this view."
                    : "No open chats in this view."}
              </p>
            )}
          {!isTicketsMode &&
            !listLoading &&
            filteredChatsForList.map((conv) => {
              const sla = isBotMessagesView ? null : chatSlaById.get(conv.id);
              const breached = Boolean(sla?.breached);
              const selected = selectedId === conv.id;
              const rowClass = breached
                ? selected
                  ? "border-l-[3px] border-l-rose-600 bg-rose-50 ring-2 ring-inset ring-rose-200/90 dark:bg-rose-950/40 dark:ring-rose-800/60"
                  : "border-l-[3px] border-l-rose-600 bg-rose-50 hover:bg-rose-100/80 dark:bg-rose-950/30 dark:hover:bg-rose-950/50"
                : selected
                  ? "border-l-[3px] border-l-teal-600 bg-teal-50 dark:bg-teal-950/40"
                  : "border-l-[3px] border-l-transparent hover:bg-white/80 dark:hover:bg-slate-800/50";
              return (
                <button
                  key={conv.id}
                  type="button"
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData("text/plain", conv.id);
                    e.dataTransfer.effectAllowed = "move";
                    setDraggingConversationId(conv.id);
                    setTeamInboxesExpanded(true);
                    setInboxChromeExpanded(true);
                  }}
                  onDragEnd={handleConversationDragEnd}
                  onClick={() => onSelectConversation(conv.id)}
                  title={
                    [breached ? sla?.reasons?.join(" · ") : null, "Hold and drag into a team folder below to move."]
                      .filter(Boolean)
                      .join(" ") || undefined
                  }
                  className={`group flex w-full touch-manipulation select-none items-start gap-3 border-b border-slate-100/80 px-4 py-3.5 text-left transition-all dark:border-slate-700/50 ${rowClass} cursor-grab active:cursor-grabbing ${
                    draggingConversationId === conv.id ? "opacity-60" : ""
                  }`}
                >
                  <div className="relative shrink-0">
                    <img
                      src={conv.user.avatar}
                      alt=""
                      className="h-11 w-11 rounded-full object-cover ring-2 ring-white shadow-md transition group-hover:ring-teal-100 dark:ring-slate-700 dark:group-hover:ring-teal-800"
                    />
                    {conv.user.online && (
                      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500 shadow-sm shadow-emerald-500/40 dark:border-slate-900" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{conv.user.name}</span>
                      <div className="flex shrink-0 items-center gap-1.5">
                        {breached && (
                          <span className="rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                            SLA
                          </span>
                        )}
                        <span className="text-[11px] font-medium text-slate-400 dark:text-slate-500">{conv.timestamp}</span>
                      </div>
                    </div>
                    <p className="truncate text-xs leading-relaxed text-slate-500 dark:text-slate-400">{conv.preview}</p>
                    {conv.workspaceName ? (
                      <p className="mt-0.5 truncate text-[10px] font-medium text-slate-400 dark:text-slate-500">
                        {conv.workspaceName}
                      </p>
                    ) : null}
                    {breached && (
                      <p className="mt-1 text-[11px] font-semibold text-rose-800 dark:text-rose-400">Needs agent response</p>
                    )}
                  </div>
                  {conv.unread > 0 && (
                    <span className="flex h-6 min-w-[1.5rem] shrink-0 items-center justify-center rounded-full bg-teal-600 px-1.5 text-[11px] font-bold text-white shadow-md">
                      {conv.unread}
                    </span>
                  )}
                </button>
              );
            })}

          {showLoadMoreConversations && (
            <div className="px-3 py-3">
              <button
                type="button"
                onClick={() => onLoadMoreConversations?.()}
                disabled={loadingMoreConversations}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                {loadingMoreConversations ? "Loading…" : "Load more"}
              </button>
              {(chatStatusFilter !== "all" || slaBreachOnly) && (
                <p className="mt-1.5 text-center text-[10px] text-slate-400 dark:text-slate-500">
                  Open/closed and SLA filters apply to loaded chats only.
                </p>
              )}
            </div>
          )}

          {isTicketsMode && ticketsLoading && (
            <div className="space-y-4 px-3 py-4">
              {[1, 2, 3].map((block) => (
                <div key={block} className="space-y-2">
                  <div className="h-3 w-24 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                  <div className="h-16 animate-pulse rounded-xl bg-slate-100 dark:bg-slate-800" />
                </div>
              ))}
            </div>
          )}
          {isTicketsMode &&
            !ticketsLoading &&
            tickets.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">No tickets yet. Create one from a chat or automation.</p>
            )}
          {isTicketsMode &&
            !ticketsLoading &&
            tickets.length > 0 &&
            slaBreachOnly &&
            ticketBreaches === 0 && (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">No SLA breaches in the ticket queue.</p>
            )}
          {isTicketsMode &&
            !ticketsLoading &&
            tickets.length > 0 &&
            !(slaBreachOnly && ticketBreaches === 0) &&
            sections.map((section) => {
              const inStatus = (tickets ?? []).filter((t) => t.status === section.id);
              let inSection = sortTicketsForInbox(inStatus, settings, now);
              if (slaBreachOnly) {
                inSection = inSection.filter((t) => ticketSlaById.get(t.id)?.breached);
              }
              const isCollapsed = Boolean(collapsedTicketSections[section.id]);
              return (
                <div key={section.id} className="border-b border-slate-100/90 last:border-b-0 dark:border-slate-700/50">
                  <button
                    type="button"
                    onClick={() =>
                      setCollapsedTicketSections((prev) => ({
                        ...prev,
                        [section.id]: !prev[section.id],
                      }))
                    }
                    className="sticky top-0 z-[1] flex w-full items-center justify-between bg-slate-50/95 px-4 py-2 text-left backdrop-blur-sm transition-colors hover:bg-slate-100/90 dark:bg-slate-900/95 dark:hover:bg-slate-800/80"
                    aria-expanded={!isCollapsed}
                  >
                    <div className="flex items-center gap-2">
                      <i
                        className={`ri-arrow-right-s-line text-sm text-slate-500 transition-transform dark:text-slate-400 ${isCollapsed ? "rotate-0" : "rotate-90"}`}
                        aria-hidden
                      />
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{section.label}</span>
                    </div>
                    <span className="text-[10px] font-semibold tabular-nums text-slate-400 dark:text-slate-500">{inSection.length}</span>
                  </button>
                  {!isCollapsed &&
                    (inSection.length === 0 ? (
                      <p className="px-4 py-3 text-center text-[11px] font-medium text-slate-400 dark:text-slate-500">No tickets in this state.</p>
                    ) : (
                      inSection.map((ticket) => {
                        const linked = (conversations ?? []).find((c) => c.id === ticket.conversationId);
                        const active = selectedTicketId === ticket.id;
                        const sla = ticketSlaById.get(ticket.id);
                        const breached = Boolean(sla?.breached);
                        const rowClass = breached
                          ? active
                            ? "border-l-[3px] border-l-rose-600 bg-rose-50 ring-2 ring-inset ring-rose-200/90 dark:bg-rose-950/40 dark:ring-rose-800/60"
                            : "border-l-[3px] border-l-rose-600 bg-rose-50 hover:bg-rose-100/80 dark:bg-rose-950/30 dark:hover:bg-rose-950/50"
                          : active
                            ? "border-l-[3px] border-l-teal-600 bg-teal-50 dark:bg-teal-950/40"
                            : "border-l-[3px] border-l-transparent hover:bg-white/80 dark:hover:bg-slate-800/50";
                        return (
                          <button
                            key={ticket.id}
                            type="button"
                            onClick={() => onSelectTicket(ticket)}
                            title={breached ? sla?.reasons?.join(" · ") : undefined}
                            className={`flex w-full flex-col gap-1 border-b border-slate-100/80 px-4 py-3.5 text-left transition-all last:border-b-0 dark:border-slate-700/50 ${rowClass}`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className={`font-mono text-sm font-bold ${breached ? "text-rose-800 dark:text-rose-400" : "text-teal-700 dark:text-teal-400"}`}>#{ticket.id}</span>
                              <div className="flex shrink-0 flex-wrap items-center justify-end gap-1">
                                {breached && (
                                  <span className="rounded-md bg-rose-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white shadow-sm">
                                    SLA
                                  </span>
                                )}
                                <span
                                  className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${ticketStatusBadgeClass(ticket.status)}`}
                                >
                                  {section.label}
                                </span>
                                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600 dark:bg-slate-700/80 dark:text-slate-300">
                                  {openedByLabel(ticket.openedBy)}
                                </span>
                              </div>
                            </div>
                            <p className="line-clamp-2 text-xs font-medium leading-snug text-slate-800 dark:text-slate-200">{ticket.subject}</p>
                            <div className="flex flex-wrap items-center gap-x-2 text-[11px] text-slate-500 dark:text-slate-400">
                              {linked && <span className="truncate">{linked.user.name}</span>}
                              <span className="text-slate-300 dark:text-slate-600">·</span>
                              <span>{new Date(ticket.createdAt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                            </div>
                            {breached && <p className="text-[11px] font-semibold text-rose-800 dark:text-rose-400">Needs agent response</p>}
                          </button>
                        );
                      })
                    ))}
                </div>
              );
            })}
        </div>
      </div>

      <div className="flex shrink-0 flex-col border-t border-slate-200/80 bg-white/95 dark:border-slate-700/60 dark:bg-slate-900/95">
        <div className="flex items-center justify-between gap-2 border-b border-slate-200/60 px-4 py-3 dark:border-slate-700/60">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">{panelTitle}</h3>
            <p className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
              {panelSubtitle} · {isTicketsMode ? "Tickets" : "Chats"} · {activeNavLabel}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {canManageTeamFolders && !isTicketsMode ? (
              <button
                type="button"
                className="ui-btn-icon !h-8 !w-8 shrink-0"
                title="New team inbox folder"
                aria-label="New team inbox folder"
                onClick={openCreateTeamInboxModal}
              >
                <i className="ri-add-line text-lg" aria-hidden />
              </button>
            ) : null}
            <button
              type="button"
              className="ui-btn-icon !h-8 !w-8 shrink-0"
              aria-expanded={inboxChromeExpanded}
              aria-controls="inbox-chrome-panel"
              title={inboxChromeExpanded ? "Hide views & folders" : "Show views & folders"}
              onClick={() => setInboxChromeExpanded((v) => !v)}
            >
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${inboxChromeExpanded ? "rotate-0" : "-rotate-90"}`}
                aria-hidden
              />
            </button>
          </div>
        </div>

        <div
          id="inbox-chrome-panel"
          hidden={!inboxChromeExpanded}
          className="max-h-[min(50vh,420px)] overflow-y-auto"
        >
          {showModeToggle && (
            <div className="border-b border-slate-200/60 px-4 py-3 dark:border-slate-700/60">
              <div className="flex rounded-xl bg-slate-100/90 p-1 ring-1 ring-slate-200/60 dark:bg-slate-800/60 dark:ring-slate-700/60" role="tablist" aria-label="Inbox type">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isTicketsMode}
                  onClick={() => onModeChange?.("chats")}
                  className={`flex-1 rounded-lg px-3 py-2 text-center text-xs font-semibold transition-all ${
                    !isTicketsMode ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Chats
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={isTicketsMode}
                  onClick={() => onModeChange?.("tickets")}
                  className={`flex-1 rounded-lg px-3 py-2 text-center text-xs font-semibold transition-all ${
                    isTicketsMode ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-600" : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                  }`}
                >
                  Tickets
                  {tickets.length > 0 && (
                    <span className="ml-1.5 rounded-md bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold text-teal-800 tabular-nums dark:bg-teal-900/60 dark:text-teal-300">
                      {tickets.length}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {!isTicketsMode ? (
            <>
              {(inboxCategories ?? []).length > 0 && (
              <div className="px-2 py-3">
                <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Views</p>
                {(inboxCategories ?? []).map((cat) => (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => onCategoryChange(cat.id)}
                    className={`mb-0.5 flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-all ${
                      activeCategory === cat.id ? "ui-pill-active" : "ui-pill-idle"
                    }`}
                  >
                    <i className={`${cat.icon} text-base opacity-80`} aria-hidden />
                    <span className="min-w-0 flex-1 truncate text-left font-medium">{cat.label}</span>
                    {cat.count > 0 && (
                      <span className="shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-500 dark:bg-slate-700/80 dark:text-slate-400">
                        {cat.count > 999 ? `${(cat.count / 1000).toFixed(0)}k` : cat.count.toLocaleString()}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              )}

              {(teamInboxes ?? []).length > 0 || canManageTeamFolders ? (
              <div className="border-t border-slate-100 px-2 py-3 dark:border-slate-700/60">
                <div className="mb-2 flex items-center justify-between gap-1 px-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Team inboxes</span>
                  <div className="flex shrink-0 items-center gap-0.5">
                    {canManageTeamFolders ? (
                      <button
                        type="button"
                        className="ui-btn-icon !h-7 !w-7"
                        title="New team inbox folder"
                        aria-label="New team inbox folder"
                        onClick={openCreateTeamInboxModal}
                      >
                        <i className="ri-add-line text-sm" aria-hidden />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="ui-btn-icon !h-7 !w-7"
                      aria-expanded={teamInboxesExpanded}
                      aria-controls="team-inboxes-nav-list"
                      title={teamInboxesExpanded ? "Collapse team inboxes" : "Expand team inboxes"}
                      onClick={() => setTeamInboxesExpanded((v) => !v)}
                    >
                      <i
                        className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${teamInboxesExpanded ? "rotate-0" : "-rotate-90"}`}
                        aria-hidden
                      />
                    </button>
                  </div>
                </div>
                <div id="team-inboxes-nav-list" hidden={!teamInboxesExpanded}>
                  {teamMoveError && (
                    <p className="mb-2 rounded-lg border border-rose-200 bg-rose-50/90 px-2 py-1.5 text-[11px] text-rose-800 dark:border-rose-800/50 dark:bg-rose-950/40 dark:text-rose-300">{teamMoveError}</p>
                  )}
                  {draggingConversationId && canPersistTeamMove && (
                    <div
                      role="note"
                      className={`mb-1.5 rounded-xl border border-dashed px-2.5 py-2 text-[11px] font-medium transition-colors ${
                        dropTargetTeamId === DROP_NONE
                          ? "border-teal-400 bg-teal-100/90 text-teal-950 ring-2 ring-teal-400 dark:bg-teal-950/40 dark:text-teal-200"
                          : "border-slate-300 bg-slate-50/90 text-slate-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-400"
                      }`}
                      onDragOver={(e) => {
                        if (!draggingConversationId || teamMoveBusy) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDragEnter={(e) => {
                        if (!draggingConversationId || teamMoveBusy) return;
                        e.preventDefault();
                        setDropTargetTeamId(DROP_NONE);
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetTeamId(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (teamMoveBusy) return;
                        const sid = e.dataTransfer.getData("text/plain");
                        if (sid) void handleMoveToTeamInbox(sid, null);
                      }}
                    >
                      <i className="ri-inbox-unarchive-line mr-1 align-text-bottom" aria-hidden />
                      Drop here to clear team folder
                    </div>
                  )}
                  {(teamInboxes ?? []).map((team) => (
                    <div
                      key={team.id}
                      className={`mb-0.5 rounded-xl transition-colors ${
                        dropTargetTeamId === team.id ? "bg-teal-100/90 ring-2 ring-teal-400 dark:bg-teal-950/40" : ""
                      }`}
                      onDragOver={(e) => {
                        if (!draggingConversationId || teamMoveBusy) return;
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDragEnter={(e) => {
                        if (!draggingConversationId || teamMoveBusy) return;
                        e.preventDefault();
                        setDropTargetTeamId(team.id);
                      }}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) setDropTargetTeamId(null);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (teamMoveBusy) return;
                        const sid = e.dataTransfer.getData("text/plain");
                        if (sid) void handleMoveToTeamInbox(sid, team.id);
                      }}
                    >
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => onCategoryChange(team.id)}
                          className={`flex min-w-0 flex-1 items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm transition-all ${
                            activeCategory === team.id ? "ui-pill-active" : "ui-pill-idle"
                          }`}
                        >
                          <i className={`${team.icon} text-base`} style={{ color: team.color }} aria-hidden />
                          <span className="min-w-0 flex-1 truncate text-left font-medium">{team.label}</span>
                          {team.count > 0 && (
                            <span className="shrink-0 text-[10px] font-semibold tabular-nums text-slate-400 dark:text-slate-500">
                              {team.count > 999 ? `${(team.count / 1000).toFixed(1)}k` : team.count.toLocaleString()}
                            </span>
                          )}
                        </button>
                        {canManageTeamFolders && team.workspaceId ? (
                          <button
                            type="button"
                            className="ui-btn-icon !h-7 !w-7 shrink-0 opacity-70 hover:opacity-100"
                            title="Edit folder"
                            aria-label={`Edit ${team.label}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFolderModalMode("edit");
                              setFolderModalTarget(team);
                              setFolderModalOpen(true);
                            }}
                          >
                            <i className="ri-pencil-line text-sm" aria-hidden />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              ) : null}
            </>
          ) : (
            <div className="border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
              <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                Every ticket opened by an agent, the bot, or the customer appears here automatically.
              </p>
            </div>
          )}
        </div>
      </div>

      <TeamInboxFolderModal
        open={folderModalOpen}
        mode={folderModalMode}
        folder={folderModalTarget}
        workspaces={workspaces}
        defaultWorkspaceId={activeWorkspaceId}
        onClose={() => setFolderModalOpen(false)}
        onSaved={() => {
          onFoldersChanged?.();
          onRetryList?.();
        }}
      />
    </div>
  );
}
